import { useCallback, Suspense, lazy, useEffect, useRef, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import type { PickupPointRow, ZoneRow } from '../lib/types';
import { money } from '../lib/format';
import { dayIn, defaultExpectedDay, nextDay } from '@delivery/shared';
import {
  ADMIN_STATUS_LABEL,
  Money,
  contactErrorLabel,
  isFeatureOn,
  ulid,
  type DictItemDto,
  type OrderStatus,
} from '@delivery/shared';
import { ContactPicker, type ContactHit } from './ContactPicker';
import { AddressSearchBox } from './AddressSearchBox';

// 地图库 250KB —— 只有真的要点位置时才加载
const LocationPicker = lazy(() =>
  import('./LocationPicker').then((m) => ({ default: m.LocationPicker })),
);

/**
 * 新建订单。
 *
 * ★ 在这之前**只能靠导入**:客服接到电话下单,没有任何办法把它录进系统。
 *   `order:create` 这个权限、`POST /orders` 这个接口一直都在,
 *   缺的只是一个入口。
 *
 * ★ 两件事必须在**保存之前**就看得见:
 *
 *   1. **这单落在哪个区域**。区域决定运费、决定默认司机 ——
 *      而它是从地址里猜出来的。猜错了要能当场换掉,
 *      不能等派单时才发现「怎么派到城东去了」。
 *
 *   2. **这单收多少钱**。计价规则在另一个页面,客服记不住;
 *      而她正拿着电话,对面在问「多少钱」。
 *
 *   两个都靠 `/pricing/rules/quote` 试算 —— 和计价页用的是同一个接口,
 *   所以不会出现「试算说 14.5、建出来 18」。
 */

interface Props {
  points: PickupPointRow[];
  zones: ZoneRow[];
  /** 租户功能开关的原始值 —— 判断走 isFeatureOn,它会回落到注册表默认值 */
  features?: Record<string, boolean>;
  /**
   * 店里的时区。
   *
   * ★ 「今天/明天」得按它算,不按这台电脑:老板在国外出差打开管理端,
   *   按浏览器时区算出来的「今天」可能是店里的昨天或明天 ——
   *   而这一单归哪天,得由店里说了算。
   */
  timezone: string;
  /**
   * 照着某一单预填 —— 「再来一单」用的。
   *
   * ★ 只是**帮他少打一遍字**,不是「确认要建重复单」。
   *   所以当日重复的那道询问照常走:同一个人一天两单很正常,
   *   但也确实可能是手滑点了两次「再来一单」——
   *   而分得出这两种的只有坐在那儿的人。
   */
  prefill?: PrefillOrder | null;
  onClose: () => void;
  onCreated: () => Promise<void>;
}

/**
 * 「再来一单」带过来的资料。
 *
 * ★ 只带**这个人是谁、货从哪发**,不带**那一单是什么**。
 *
 *   件数、重量、代收、特殊物品、备注 —— 这些是上一单的事实,
 *   不是这个人的属性。「客户要求下午 3 点后再送」跟到新单上就是错的,
 *   而「代收 120」跟过来更糟:司机会照着去收一笔本来不存在的钱。
 *   用户原话:「那些备注之类的,不能有这样吧……资料就是用当前收件人的各种资料」。
 *
 * ★ 取货点和商家带过来,因为它们是**必填**的,而且「从哪发」
 *   通常和上一单一样 —— 不带的话人还得重选一遍,那就不省事了。
 */
export interface PrefillOrder {
  receiverName: string;
  receiverPhone: string;
  receiverAddress: string;
  /** 门牌 / 楼层 —— 属于这个地址,不属于某一单 */
  receiverLat?: number | null;
  receiverLng?: number | null;
  pickupPointId?: string | null;
  customerId?: string | null;
}

interface Quote {
  matched: boolean;
  reason?: string;
  customerPrice?: number;
  breakdown?: { lines: Array<{ code: string; label: string; amount: number }> };
}

export function CreateOrderDialog({
  points,
  zones,
  features,
  timezone,
  prefill,
  onClose,
  onCreated,
}: Props) {
  const specialItemsOn = isFeatureOn(features, 'specialItems');
  /*
   * ★ 商家要能选。
   *
   *   在这之前商家只能**从取货点带出来** —— 于是没有取货点的商家
   *   根本下不了单;而现在「这一单收多少」正是按商家 × 大区算的,
   *   选错商家等于报错价。
   *
   *   自己去拉一份,不用调用方传:这个弹窗已经自己拉过取货点了
   *   (见下面那个 useEffect 的说明),多一个 props 只会多一处忘记传。
   */
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);
  /** 商家列表没读到 —— 要说出来,不能让它看起来像「一家商家都没有」 */
  const [customersFailed, setCustomersFailed] = useState(false);
  const [customerId, setCustomerId] = useState(prefill?.customerId ?? '');
  /*
   * ★ 人手动选过商家没有 —— 选过之后就不再跟着取货点变。
   *   「再来一单」带过来的商家算**选过**:原单是哪家就是哪家,
   *   不能让取货点那个 effect 把它改掉(代发、代收这些情况下
   *   取货点和商家本来就不是一家)。
   */
  const customerTouched = useRef(prefill?.customerId != null);

  useEffect(() => {
    void api<Array<{ id: string; name: string }> | { data: Array<{ id: string; name: string }> }>(
      '/customers',
    )
      .then((r) => {
        const list = Array.isArray(r) ? r : r.data;
        setCustomers(list);
        /*
         * ★ 只有一家商家时直接选上。
         *
         *   多数租户就是一家自营。让人在只有一个选项的下拉框里
         *   每单点一次,不是「让他确认」,只是多一次可以忘掉的操作 ——
         *   而忘掉的后果不是空着:默认值是「不指定(散客)」,
         *   那一单会按兜底价算,而不是这家商家谈好的分区价。
         *
         *   只在**恰好一家**时这么做。两家以上就该由人来选:
         *   替他挑第一个等于替他决定这一单收多少钱。
         */
        if (list.length !== 1) return;
        setCustomerId((cur) => (cur === '' && !customerTouched.current ? list[0]!.id : cur));
      })
      .catch(() => setCustomersFailed(true));
  }, []);
  const [f, setF] = useState({
    pickupPointId: prefill?.pickupPointId ?? points[0]?.id ?? '',
    receiverName: prefill?.receiverName ?? '',
    receiverPhone: prefill?.receiverPhone ?? '',
    receiverAddress: prefill?.receiverAddress ?? '',
    receiverPostcode: '',
    receiverNote: '',
    itemDescription: '',
    // 空字符串是**输入中**的合法中间态(删光了还没开始打),离开框时补回 1
    packageCount: 1 as number | '',
    weightGram: '' as number | '',
    /*
     * ★ 这里存的是**元**的原文,不是分。
     *   让人把 120 块钱敲成 12000,是在要求他每次心算一次 ——
     *   而心算错的那次,司机会去收 1200 块。
     *   库里仍然按最小单位存(结算、守卫、账单都按它算),
     *   换算在提交这一处做一次,用 Money.fromMajor(不碰浮点)。
     */
    codAmount: '',
    zoneId: '',
    remark: '',
  });
  /*
   * ★ 取货点默认值不能只在 useState 里取一次。
   *   弹窗开得比取货点列表加载得快的时候(刷新完页面立刻点「新建订单」就会),
   *   初值是 '',等列表回来了状态还是 '' —— 于是 canSave 永远 false,
   *   「建单」按钮一直灰着,而下拉框因为 value='' 对不上任何 option,
   *   浏览器照样把第一项显示成选中的。看起来全填好了却按不下去,
   *   没人猜得到是差这一个 id。
   */
  useEffect(() => {
    if (points.length === 0) return;
    setF((s) => (s.pickupPointId === '' ? { ...s, pickupPointId: points[0]!.id } : s));
  }, [points]);

  /*
   * ★ 取货点通常就决定了商家(店自己发货),所以选了取货点就把商家带出来 ——
   *   但人**手动改过商家之后就别再动它**:代发、代收这些情况下
   *   取货点和商家本来就不是一家。
   */
  useEffect(() => {
    if (customerTouched.current) return;
    const owner = points.find((x) => x.id === f.pickupPointId)?.customer?.publicId;
    if (owner) setCustomerId(owner);
  }, [f.pickupPointId, points]);

  /*
   * ★ 「货到付款」是一个**明确的勾**,而不是「金额填了就算」。
   *
   *   原来只有一个「代收货款(分)」输入框,提示写着「不代收就留空」——
   *   于是漏填和「这单不代收」长得一模一样,而两者的后果差着一整单货款:
   *   司机不会去要钱,货给出去了,钱没了。
   *   勾了就必须填金额,建单按钮在填之前是灰的。
   */
  const [cod, setCod] = useState(false);
  /*
   * ★ 「要收,但现在还不知道收多少」。
   *
   *   用户原话:框子上的小票要在**算出金额之前**就打出来贴上 ——
   *   它是拿来认「这框是谁的单」的。所以允许只勾代收不填金额:
   *   小票上金额那栏留一条空线等人手写,司机送到时按商家确认的数录进来。
   */
  const [codPending, setCodPending] = useState(false);
  /** 冻品、易碎…… 分类是每家自己在设置里加的(字典) */
  const [specialItems, setSpecialItems] = useState<string[]>([]);
  /*
   * ★ 「今天/明天」按**店里**的时区算,不按这台电脑。
   *
   *   老板在国外出差打开管理端,按浏览器时区算出来的「今天」可能是
   *   店里的昨天或明天 —— 而这一单归哪天,得由店里说了算。
   */
  const todayStr = dayIn(timezone);
  const tomorrowStr = nextDay(todayStr);

  /**
   * 期望送达日期。
   *
   * ★ 一进来就预选**按规则算出来的那一天**:当日达,而傍晚 6 点之后
   *   录的单预选次日达(和服务端同一个函数、同一个时区,不会算出两个答案)。
   *
   *   以前这里多一档「默认」,和「当日达」并列 —— 而默认本来就是当日达,
   *   两个按钮说的是同一件事。真正要让人看见的不是「有没有用默认」,
   *   是**这一单到底定在哪一天**:晚上 7 点录单时预选的是明天,
   *   他一眼就看得见,想改成今天也点得到。
   */
  const [expectedDate, setExpectedDate] = useState(() => defaultExpectedDay(timezone));
  const [specialDict, setSpecialDict] = useState<DictItemDto[]>([]);

  useEffect(() => {
    if (!specialItemsOn) return;
    void api<DictItemDto[]>('/dict/SPECIAL_ITEM')
      .then(setSpecialDict)
      .catch(() => undefined);
  }, [specialItemsOn]);

  /*
   * ★ 「元」转最小单位只做**一次**,而且只在这里做。
   *   散在提交和试算两处的话,总有一天两处除法不一样 ——
   *   而那时候预览的价和建出来的价对不上,谁也说不清哪个才作数。
   */
  /*
   * ★ 用 `tryFromMajor`,**不能用 fromMajor**。
   *   后者对「120.」这种敲了一半的输入会抛 —— 而这是在 render 里,
   *   一抛整棵树就被卸载:用户看到的是页面自己刷新了一下、填的全没了,
   *   而他做的只是敲了一个小数点。
   */
  const codMinor = cod ? (Money.tryFromMajor(f.codAmount) ?? 0) : 0;

  /*
   * ★ 填完电话就去通讯录里对一眼「这个号是谁」。
   *
   *   命中同一个号、而名字对不上时,是两件完全不同的事:
   *     · 同一个人换了称呼(「王先生」→「王小明」)—— 该更新通讯录
   *     · **号码换主人了** —— 那这一单的地址、上次价、错误标记全都不该沿用
   *   系统分不出来,但客服看一眼就知道。所以摆出来问,不替她选。
   *
   *   查的是自己的库,不花钱;停手 500ms 才查,别每敲一个字打一次。
   */
  const [samePhone, setSamePhone] = useState<ContactHit | null>(null);
  /** 人明确说了「按我填的改」——建单时把通讯录里的名字一起更新 */
  const [renameContact, setRenameContact] = useState(false);

  const [picked, setPicked] = useState<ContactHit | null>(null);
  /*
   * ★ 运费这一栏的三种来源,要在界面上分得清:
   *
   *   · QUOTE   —— 计价规则试算出来的。**不往后端传**,让后端自己算:
   *                前后端各算一次的话,规则一改就会出现两个数,
   *                而谁也说不清哪个才作数
   *   · CONTACT —— 这个收件人上次收的钱。老客户的价往往是谈好的,
   *                和规则算出来的不是一个数
   *   · MANUAL  —— 人自己改的。改过就以他为准,绝不再被试算盖回去
   */
  /** 区域那一行展开了没 —— 默认收起,见下面那段说明 */
  const [zoneOpen, setZoneOpen] = useState(false);
  const [priceSource, setPriceSource] = useState<'QUOTE' | 'CONTACT' | 'MANUAL'>('QUOTE');
  /*
   * ★ 用 ref 跟住它,给异步回调读。
   *   放进那个 effect 的依赖数组的话,每改一次价都会重新去搜一遍通讯录;
   *   而在回调里直接读闭包捕获的 priceSource 又会读到过期的值 ——
   *   于是人刚改完价,一个在路上的响应就把它盖回去了。
   */
  const priceSourceRef = useRef(priceSource);
  priceSourceRef.current = priceSource;
  /** 提交用的值,最小单位 */
  const [price, setPrice] = useState<number | ''>('');
  /*
   * ★ 界面上按**元**显示和输入,和代收金额统一。
   *   一处填元、一处填分的话,人迟早会在其中一处差 100 倍 ——
   *   而「这单收 500」看起来像 500 块,实际是 5 块。
   */
  const [priceText, setPriceText] = useState('');
  /*
   * ★ 坐标也带过来 —— 同一个地址不用再搜一遍。
   *   不带的话这一单会进复核队列:上不了地图、司机导航不了,
   *   而地址明明是刚从一张好单上抄下来的。
   */
  const [lat, setLat] = useState<number | null>(prefill?.receiverLat ?? null);
  const [lng, setLng] = useState<number | null>(prefill?.receiverLng ?? null);
  const addressBoxRef = useRef<HTMLDivElement | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [quote, setQuote] = useState<Quote | null>(null);

  /*
   * ★ 手工改过价、或者用了收件人上次的价之后,试算结果**不再自动盖回去**
   *   —— 这是对的:那是人做过的决定。
   *
   *   但换了商家(或改了地址落到别的大区)之后,规则算出来的数变了,
   *   而屏幕上还是旧的那个,**什么提示都没有**。
   *   用户报的「换商家之后下面价格也没变」就是这个:
   *   系统没错,但它把一件已经变了的事藏起来了。
   */
  const useQuotedPrice = useCallback(() => {
    const v = quote?.customerPrice ?? '';
    if (v === '') return;
    setPriceSource('QUOTE');
    setPrice(v);
    setPriceText(Money.toMajorString(Money.minor(v)));
  }, [quote]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /*
   * ★ 这一次下单的标识,**弹窗打开时生成一次**,重试不换。
   *
   *   线上出过:同一张单被下了两遍,司机在池子里看到两张一样的,接了两次。
   *   `disabled={busy}` 只挡得住极快的双击 —— 挡不住真正的那条路:
   *   请求已经到服务端并且成功了,而响应在路上丢了(切基站、电梯、地铁),
   *   客服看到「建单失败」,于是她再点一次。
   *
   *   键不变,服务端就能认出「这是同一次下单」,把第一次那张返回来。
   *   用 ref 不用 state:它绝不该因为渲染而变。
   */
  const idemKey = useRef(ulid());
  /** 服务端说这个人今天已经有没走完的单了 —— 摆出来问一句 */
  const [dupWarn, setDupWarn] = useState<{
    orderNo: string;
    status: string;
    createdAt: string;
  } | null>(null);

  /*
   * ★ 试算跟着「会影响价格的字段」走,不是每敲一个字都算。
   *   地址、姓名这些改动不影响价,跟着算只是白打接口。
   */
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      void api<Quote>('/pricing/rules/quote', {
        method: 'POST',
        body: {
          // ★ 试算也要带商家 —— 不带的话预览的是兜底价,
          //   而建出来按这家谈好的价:客服在电话里报的数和账单对不上
          customerId: customerId || null,
          zoneId: f.zoneId || null,
          /*
           * ★ 把地址也带上。
           *   不带的话:客服只填地址、没手动选区 → 试算里 zoneId 是空的
           *   → 落到兜底价($5),而真正建单时后端拿地址匹配出「北岸」
           *   → 按这家谈的 $7 存。客服在电话里报的数和账单对不上,
           *   而两边都不报错。
           */
          receiverAddress: f.receiverAddress || null,
          receiverPostcode: f.receiverPostcode || null,
          // 坐标落在画好的范围里、或者这个人定过区,判区结果和建单时一样
          receiverLat: lat,
          receiverLng: lng,
          receiverPhone: f.receiverPhone || null,
          weightGram: f.weightGram === '' ? 0 : f.weightGram,
          // 框里空着的时候按 1 试算 —— 不然价会跟着闪一下
          packageCount: f.packageCount === '' ? 1 : f.packageCount,
          codAmount: codMinor,
        },
      })
        .then((q) => {
          if (cancelled) return;
          setQuote(q);
          // 人改过、或者用的是这个收件人上次的价,就别拿试算盖回去
          if (priceSource === 'QUOTE') {
            const v = q.customerPrice ?? '';
            setPrice(v);
            setPriceText(v === '' ? '' : Money.toMajorString(Money.minor(v)));
          }
        })
        .catch(() => !cancelled && setQuote(null));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    f.zoneId,
    // 地址变了,落在哪个区可能就变了 —— 而区决定按哪条规则算
    f.receiverAddress,
    f.receiverPostcode,
    lat,
    lng,
    f.receiverPhone,
    f.weightGram,
    f.packageCount,
    f.codAmount,
    cod,
    priceSource,
    customerId,
  ]);

  /**
   * 选中通讯录里的人 —— 姓名/电话/地址/门牌/坐标一次填全。
   *
   * ★ 坐标是这一下的重点。没坐标的单上不了地图、司机导航不了,
   *   而这个人上次建单时定过的坐标就存在通讯录里 —— 不用它等于白存。
   */
  function pickContact(c: ContactHit) {
    setPicked(c);
    setF((s) => ({
      ...s,
      receiverName: c.name,
      receiverPhone: c.phone,
      receiverAddress: [c.address, c.addressDetail].filter(Boolean).join(' '),
    }));
    setLat(c.lat);
    setLng(c.lng);
    if (c.lat != null && c.lng != null) {
      setErr(null);
    }
    /*
     * ★ 上次给这个人收多少,这次默认还收多少。
     *   老客户的价是谈好的,计价规则算不出来 ——
     *   而客服记不住每个人谈的是多少,只能翻历史订单。
     */
    if (c.lastPrice != null && c.lastPrice > 0) {
      setPrice(c.lastPrice);
      setPriceText(Money.toMajorString(Money.minor(c.lastPrice)));
      setPriceSource('CONTACT');
    }
  }

  /*
   * ★ 必须**选到取货点**才能按。
   *
   *   原来这里是「选了取货点 **或者** 一个取货点都没有」——
   *   而这个弹窗根本没有手填取货地址的输入框,
   *   没有取货点时建出来的单必然被后端顶回来
   *   (「请选择取货点或填写取货地址」)。
   *
   *   更要命的是它顺带制造了一个时序坑:取货点列表还在路上时
   *   `points` 也是空的,于是**按钮在列表回来之前就是可按的**,
   *   手快的人点下去,拿到的是一句看不懂的报错 ——
   *   而下拉框那会儿已经画得好好的了。
   */
  useEffect(() => {
    const digits = f.receiverPhone.replace(/[^\d]/g, '');
    if (digits.length < 6) {
      setSamePhone(null);
      return;
    }
    const t = setTimeout(() => {
      void api<{ data: ContactHit[] } | ContactHit[]>(
        `/contacts/search?q=${encodeURIComponent(f.receiverPhone.trim())}&limit=5`,
      )
        .then((r) => {
          const list = Array.isArray(r) ? r : r.data;
          // 只认**号码完全一样**的那条 —— 模糊命中的不算「同一个人」
          const hit = list.find((c) => c.phone.replace(/[^\d]/g, '') === digits);
          setSamePhone(hit ?? null);
          /*
           * ★ 手打手机号也要带出上次谈好的价。
           *
           *   原来只有**从通讯录点选**才带 —— 而客服接电话时手更快的是
           *   直接把号码敲进去。于是「上次给他收 12 块」这件事只有
           *   走对了路径才生效,而两条路径在客服眼里是同一件事。
           *
           *   只在人**还没动过价**时套用(priceSource 仍是 QUOTE)——
           *   已经改过的价不能被一次异步返回悄悄盖掉。
           */
          if (hit?.lastPrice != null && hit.lastPrice > 0 && priceSourceRef.current === 'QUOTE') {
            setPicked((cur) => cur ?? hit);
            setPrice(hit.lastPrice);
            setPriceText(Money.toMajorString(Money.minor(hit.lastPrice)));
            setPriceSource('CONTACT');
          }
        })
        .catch(() => setSamePhone(null));
    }, 500);
    return () => clearTimeout(t);
  }, [f.receiverPhone]);

  /*
   * ★ 名字一改就把「按我填的改通讯录」这个决定清掉。
   *   不清的话:她先点了「按我填的改」,又改成第三个名字 ——
   *   而系统会拿最后那个名字去覆盖通讯录,那不是她表过态的那件事。
   */
  useEffect(() => {
    setRenameContact(false);
  }, [f.receiverName]);

  /*
   * ★ 名字一改就把「按我填的改通讯录」这个决定清掉。
   *   不清的话:她先点了「按我填的改」,又改成第三个名字 ——
   *   而系统会拿最后那个名字去覆盖通讯录,那不是她表过态的那件事。
   */
  useEffect(() => {
    setRenameContact(false);
  }, [f.receiverName]);

  const canSave =
    f.receiverName.trim() !== '' &&
    f.receiverPhone.trim() !== '' &&
    f.receiverAddress.trim().length >= 5 &&
    f.pickupPointId !== '' &&
    /*
     * ★ 勾了货到付款却没填金额 = 司机不知道该收多少。宁可按不下去 ——
     *   除非人**明确**勾了「金额待定」:那是一个说得出口的选择,
     *   而漏填不是。
     */
    !(cod && !codPending && !(Number(f.codAmount) > 0));

  async function save(confirmed = false) {
    if (lat == null || lng == null) {
      setErr('需要从地址的搜索数据中选择实际地址');
      addressBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setBusy(true);
    setErr(null);
    setDupWarn(null);
    try {
      const r = await api<{ orderNo: string; deduped?: boolean }>('/orders', {
        method: 'POST',
        body: {
          idempotencyKey: idemKey.current,
          // 人看过「今天已经给他建过一单」还是要建
          ...(confirmed ? { confirmDuplicate: true } : {}),
          ...(f.pickupPointId ? { pickupPointId: f.pickupPointId } : {}),
          ...(customerId ? { customerId } : {}),
          ...(renameContact ? { updateContactName: true } : {}),
          receiverName: f.receiverName.trim(),
          receiverPhone: f.receiverPhone.trim(),
          receiverAddress: f.receiverAddress.trim(),
          ...(f.receiverPostcode ? { receiverPostcode: f.receiverPostcode } : {}),
          ...(f.receiverNote ? { receiverNote: f.receiverNote } : {}),
          ...(f.itemDescription ? { itemDescription: f.itemDescription } : {}),
          packageCount: f.packageCount === '' ? 1 : f.packageCount,
          ...(f.weightGram !== '' ? { weightGram: f.weightGram } : {}),
          /*
           * ★ 期望送达日期总是明着传 —— 界面上预选的那一天就是要落库的那一天。
           *
           *   预选值和服务端的默认值是**同一个函数、同一个时区**算出来的
           *   (shared 的 defaultExpectedDay + 店里的 timezone),所以两边
           *   不会给出不同的答案。传过去而不是留空,是为了让「屏幕上写的」
           *   和「存进去的」一定是同一件事 —— 中间隔着一次服务端重算的话,
           *   跨过 18 点那一刻提交,人看到的和存下的就会差一天。
           */
          expectedDeliveryDate: expectedDate,
          // ★ 没勾「货到付款」就是 0 —— 勾掉之后残留的金额不能跟着发出去
          codAmount: cod && codPending ? 0 : codMinor,
          ...(cod && codPending ? { codPending: true } : {}),
          ...(specialItems.length > 0 ? { specialItems } : {}),
          ...(f.zoneId ? { zoneId: f.zoneId } : {}),
          ...(lat != null && lng != null ? { receiverLat: lat, receiverLng: lng } : {}),
          /*
           * ★ 试算出来的价**不传** —— 留空才会走计价规则(见下面那段注释)。
           *   传的话等于把前端这一刻算出来的数固化进订单,
           *   而后端才是唯一说得清「为什么是这个价」的地方(明细、规则 id)。
           *   人改过的、或者沿用上次的,才是明确的意图,要传。
           */
          ...(priceSource !== 'QUOTE' && price !== '' ? { customerPrice: price } : {}),
          ...(f.remark ? { remark: f.remark } : {}),
          /*
           * ★ 金额一个都不传 —— 留空才会走计价规则。
           *   传 0 的含义是「这一单免费」(见 dto.ts),
           *   而这正是轮次 62 在导入那条路径上踩过的坑。
           */
        },
      });
      /*
       * ★ 服务端认出这是同一次下单(deduped)时说一句 ——
       *   否则客服以为自己刚才那次真的失败了,而实际上单早就建好了,
       *   她会去列表里找一张「新的」,找不到又建一次。
       */
      if (r.deduped) setErr(`这一单刚才已经建过了(${r.orderNo})—— 没有重复建`);
      await onCreated();
    } catch (e) {
      /*
       * ★ 「今天这个人已经有没走完的单」不是错误,是一个**问句**。
       *   同一个人上午一单下午一单完全正常,系统答不了这个 ——
       *   所以把已有的那一张摆出来(单号、几点建的、到哪一步了),
       *   由看得见全貌的人来定。
       */
      if (e instanceof ApiFailure && e.code === 'DUPLICATE_ORDER_SUSPECTED') {
        const d = (e.error.details ?? {}) as Record<string, string>;
        setDupWarn({
          orderNo: d['orderNo'] ?? '',
          status: d['status'] ?? '',
          createdAt: d['createdAt'] ?? '',
        });
      } else {
        setErr(e instanceof ApiFailure ? e.message : '建单失败');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    /*
      ★ 手机上这一屏踩过三件事,都在这个外壳里:

        1. 高度用 `dvh` 不用 `vh`。手机浏览器地址栏收起时 `vh` 才等于可见高度,
           展开时 94vh 比屏幕还高 —— 底部的「建单 / 取消」被浏览器 UI 盖住,
           而人不知道是要往下滑还是这单建不了。
           ★ 这一条**测不出来**:无头浏览器没有地址栏,vh 和 dvh 在里面等价。
             下面那几条用例保的是「头尾固定」,不是这一条。

        2. 标题和按钮不能跟着内容一起滚。原来整个面板是一个滚动区,
           填到一半想取消就得先滚回顶部 —— 电脑上只是麻烦,
           手机上这一屏十几个字段,等于按钮消失了。

        3. 底部留安全区。全面屏那条横杠会盖住最后一行,而最后一行正好是「建单」。
    */
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-3" onClick={onClose}>
      <div
        className="flex max-h-[94dvh] w-full max-w-2xl flex-col overflow-hidden rounded-xl
                   bg-white dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头:固定 */}
        <div
          className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-100
                        px-4 py-2 dark:border-slate-700"
        >
          {/*
            ★ 标题下面那句「客服接电话下单,或者补录一张纸质单」去掉了。
              它解释的是这个弹窗**是干什么的** —— 而能打开它的人早就知道了。
              这一屏在手机上本来就长,顶部每一行都是从表单那里借来的。
          */}
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">新建订单</h2>
          {/*
            ★ 一个明确的关闭按钮。原来只能点遮罩或者滚到底找「取消」——
              而遮罩在手机上只剩边上十几个像素,点不中。
          */}
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="-mr-1 -mt-1 rounded-lg p-2 text-xl leading-none text-slate-400
                       hover:bg-slate-100 hover:text-slate-600
                       dark:hover:bg-slate-700 dark:hover:text-slate-200"
          >
            ×
          </button>
        </div>

        {/* 身:只有这一段滚 */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-2">
          {/*
          ★ 搜索框放在**最上面**,不是塞在某个字段旁边。
            建单的第一个动作永远是「这个人以前来过吗」——
            答案是「来过」的时候,下面整张表都不用填了。
        */}
          <div className="mb-2 rounded-lg bg-slate-50 px-2.5 py-2 dark:bg-slate-900/50">
            <ContactPicker onPick={pickContact} />
            {picked && (
              <div className="mt-2 text-xs text-slate-500">
                已填入通讯录里的「{picked.name}」
                {picked.lat != null ? ',坐标一并带过来了' : ' —— 这条没有坐标,记得在下面点一下位置'}
                {/*
                ★ 用旧号码搜到人的时候,要说的是**「现在用的是哪个号」**,
                  而不是「电话有问题」。
                  后者会让客服以为刚填进去的这个新号也有问题 ——
                  而事实恰恰相反:有问题的是她搜的那个旧号。
              */}
                {picked.matchedByWrongPhone && (
                  <span className="ml-1 text-amber-700 dark:text-amber-400">
                    · 你搜的 {picked.matchedByWrongPhone} 是旧号码,已填的是现在这个 {picked.phone}
                  </span>
                )}
                {picked.errorTag && (
                  <span className="ml-1 text-red-600 dark:text-red-400">
                    · 这条被标记过:{contactErrorLabel(picked.errorTag)}
                    {picked.errorTag !== 'ADDRESS' ? `(${picked.phone})` : ''}
                    {picked.errorNote ? ` ${picked.errorNote}` : ''}
                  </span>
                )}
                {/*
                ★ 这个人身上的常驻备注,在**建单之前**就说出来。
 
                  它会由服务端并进这一单的备注里 —— 不说的话,客服
                  会把同一句门禁密码再打一遍(于是票上印两遍),
                  或者以为没带上而去联系人那边翻一次。
 
                ★ 只显示、不往输入框里塞:换一个收件人时,
                  塞进去的字得从文本里抠出来,抠错就把客服刚打的话吞掉。
              */}
                {picked.deliveryNote && (
                  <div className="mt-1 text-brand-700 dark:text-brand-400">
                    · 这个人有常驻备注:{picked.deliveryNote} —— 会自动并进这一单的备注
                  </div>
                )}
              </div>
            )}
          </div>

          {/*
          ★ 六栏栅格,字段按**实际需要的宽度**占格,而不是一律半行。
            一律半行的话,邮编这种四个字的框和地址一样宽,
            而下面就会出现一个空格子 —— 白占一行。
            这一屏字段多,每空一行就多滚一次,而客服正拿着电话。
        */}
          {/*
          ★ 十二栏,而且**手机上也用同一套**(不是 sm: 之后才生效)。
    
            原来窄屏退回单列,于是「收件人」和「电话」各占一整行 ——
            两个七八个字的框摊成 350px 宽,而下面的表更长了,
            填一单要滚三屏。手机上这几个字段本来就短,并排放得下。
        */}
          <div className="grid grid-cols-12 gap-x-2 gap-y-2 sm:gap-x-3">
            <div className="col-span-6">
              <Field label="收件人" required>
                <input
                  aria-label="收件人"
                  value={f.receiverName}
                  onChange={(e) => setF({ ...f, receiverName: e.target.value })}
                  className={INPUT}
                />
              </Field>
            </div>
            <div className="col-span-6">
              <Field label="手机号" required hint="会按租户所在国家补区号">
                <input
                  aria-label="手机号"
                  value={f.receiverPhone}
                  onChange={(e) => setF({ ...f, receiverPhone: e.target.value })}
                  className={INPUT}
                />
              </Field>
            </div>

            {/*
            ★ 同一个号、名字对不上 —— 把两个名字都摆出来问一句。
    
              这有两种可能,而它们的处理方式完全相反:
                · 同一个人换了称呼(「王先生」→「王小明」)—— 该更新通讯录
                · **号码换主人了** —— 那这一条通讯录里的地址、上次价、
                  错误标记全都不该沿用到这一单上
              系统分不出来,客服看一眼就知道。所以摆出来,不替她选。
    
            ★ 不选也能建单:默认**不动**通讯录里的名字 ——
              「不确定」的时候什么都不改,是唯一不会造成损失的选择。
          */}
            {samePhone &&
              samePhone.name.trim() !== f.receiverName.trim() &&
              f.receiverName.trim() !== '' && (
                <div
                  aria-label="同号不同名"
                  className="col-span-12 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2
                         text-xs dark:border-amber-800 dark:bg-amber-950/40"
                >
                  <div className="text-amber-800 dark:text-amber-300">
                    这个号码通讯录里已经有了,记的是「
                    <span className="font-medium">{samePhone.name}</span>」, 而你填的是「
                    <span className="font-medium">{f.receiverName}</span>」
                    {samePhone.errorTag && (
                      <span className="ml-1 text-red-600 dark:text-red-400">
                        · 而且被标记过:{contactErrorLabel(samePhone.errorTag)}
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setF((s0) => ({ ...s0, receiverName: samePhone.name }));
                        setRenameContact(false);
                      }}
                      className="rounded border border-amber-400 px-2 py-1 text-amber-800
                             dark:text-amber-300"
                    >
                      用通讯录里的「{samePhone.name}」
                    </button>
                    <label className="flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
                      <input
                        type="checkbox"
                        aria-label="把通讯录里的名字也改成我填的"
                        checked={renameContact}
                        onChange={(e) => setRenameContact(e.target.checked)}
                      />
                      按我填的,顺便把通讯录改了
                    </label>
                    {/* ★ 第三种可能:根本不是同一个人。这时候两边都不该动 */}
                    <span className="text-amber-700/80 dark:text-amber-400/70">
                      都不选也行 —— 不是同一个人的话,通讯录不该被改
                    </span>
                  </div>
                </div>
              )}

            <div className="col-span-12" ref={addressBoxRef}>
              <Field label="收货地址" required hint="至少 5 个字。区域按地址自动匹配">
                <input
                  aria-label="收货地址"
                  value={f.receiverAddress}
                  onChange={(e) => {
                    setF({ ...f, receiverAddress: e.target.value });
                    setLat(null);
                    setLng(null);
                  }}
                  className={INPUT}
                />
              </Field>
              <AddressSearchBox
                query={f.receiverAddress}
                onPick={(c) => {
                  setF((s) => ({ ...s, receiverAddress: c.address }));
                  setLat(c.lat);
                  setLng(c.lng);
                  setErr(null);
                }}
              />
            </div>

            {/*
            ★ 商家决定两件事:这一单**收多少**(商家 × 大区 的基础价),
              以及取货**要不要拍照**。所以它得摆在明面上,而不是
              从取货点悄悄带出来 —— 带错了两样都错。
          */}
            <div className="col-span-3">
              <Field
                label="商家"
                hint={
                  customersFailed
                    ? '★ 商家列表没读到 —— 这样建出来的单按兜底价算,刷新一下再建'
                    : '决定价和拍照'
                }
              >
                <select
                  aria-label="商家"
                  value={customerId}
                  onChange={(e) => {
                    customerTouched.current = true;
                    setCustomerId(e.target.value);
                  }}
                  className={INPUT}
                >
                  <option value="">不指定(散客)</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="col-span-3">
              <Field
                label="取货点"
                hint={points.length === 0 ? '还在读取货点…读完才能建单' : undefined}
              >
                <select
                  aria-label="取货点"
                  value={f.pickupPointId}
                  onChange={(e) => setF({ ...f, pickupPointId: e.target.value })}
                  className={INPUT}
                >
                  {/*
                ★ 列表空着的时候摆一个占位项。
                  只留一个空 select 的话,浏览器会把它画成一个空白框,
                  而人分不清「还在读」和「一个取货点都没有」。
              */}
                  {points.length === 0 && <option value="">读取中…</option>}
                  {points.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="col-span-3">
              <Field label="件数">
                {/*
              ★ 允许框里**暂时是空的**。
                原来是 `Math.max(1, Number(v) || 1)`:删到空的那一刻
                `Number('')` 是 0、`|| 1` 又把它变回 1 —— 于是这个框
                **删不掉**。想从 1 改成 12,唯一的办法是把光标移到 1 前面
                敲一个 1(变成 "11"?不,是把 1 挪到后面)—— 人只能发现
                「得先打字再回头删」,而不知道为什么。

                输入过程中不夹逼,离开框时才补 —— 边打边纠正的框,
                打到一半数字会自己跳。
            */}
                <input
                  type="number"
                  aria-label="件数"
                  min={1}
                  value={f.packageCount}
                  onChange={(e) =>
                    setF({
                      ...f,
                      packageCount: e.target.value === '' ? '' : Number(e.target.value),
                    })
                  }
                  onBlur={() =>
                    setF((s) => ({
                      ...s,
                      // 空着或填了 0 / 负数就回到 1 —— 一单至少有一件
                      packageCount:
                        s.packageCount === '' || Number(s.packageCount) < 1
                          ? 1
                          : Math.floor(Number(s.packageCount)),
                    }))
                  }
                  className={INPUT}
                />
              </Field>
            </div>
            <div className="col-span-3">
              <Field label="重量(克)" hint="影响运费。不填按最低档算">
                <input
                  type="number"
                  aria-label="重量(克)"
                  value={f.weightGram}
                  onChange={(e) =>
                    setF({ ...f, weightGram: e.target.value === '' ? '' : Number(e.target.value) })
                  }
                  className={INPUT}
                />
              </Field>
            </div>
            {/*
            ★ 期望送达日期。
              用户原话:「默认就是当日达,然后可以选次日达. 然后增加一个日期选择.
              下午六点之后下的单默认就是次日达」。

            ★ 三个按钮里**默认那个是选中的**,而它的含义是「交给规则定」——
              不是「今天」。晚上 7 点录单时,默认那一档给出的是明天,
              而界面上会把算出来的那天写在旁边,人看得见。
          */}
            <div className="col-span-6">
              <Field
                label="期望送达"
                hint={
                  expectedDate === todayStr
                    ? '今天送到。过了今天还没送完,司机端会提醒他'
                    : expectedDate === tomorrowStr
                      ? '明天送到 —— 今天不会出现在司机的当日列表里'
                      : `定在 ${expectedDate || '—'}`
                }
              >
                <div className="flex flex-wrap gap-2">
                  {[
                    [todayStr, '当日达'],
                    [tomorrowStr, '次日达'],
                  ].map(([v, label]) => (
                    <button
                      key={label}
                      type="button"
                      aria-label={label}
                      onClick={() => setExpectedDate(v!)}
                      className={`h-10 px-3 rounded-lg border text-sm ${
                        expectedDate === v
                          ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                          : 'border-slate-300 text-slate-700 dark:border-slate-600 dark:text-slate-300'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                  <input
                    type="date"
                    aria-label="期望送达日期"
                    value={expectedDate}
                    onChange={(e) => setExpectedDate(e.target.value)}
                    className={`${INPUT} w-auto flex-1 min-w-[9rem]`}
                  />
                </div>
              </Field>
            </div>
            {/*
            ★ 邮编这个框去掉了。

              生产上按邮编匹配的规则是**零条**(183 条整词 + 8 条包含,全是地名),
              真实订单里也没有一张填过它 —— 一个永远空着、又要客服每单扫过去的框。
              字段本身留着(接口仍然收 receiverPostcode,历史单也还有值),
              哪天真配了邮编规则,把这个框加回来即可。

            ★ 运费搬到这个位置。
              它是客服**当场要报给电话那头**的数,原来压在整张表最下面、
              手机上要滚到底才看得见。而它上面那一行是件数和重量 ——
              正好是决定这个数的两个输入,挨着看才对得上。
          */}
            <div className="col-span-4">
              <Field label="运费(元)" emphasis>
                <input
                  id="co-price"
                  aria-label="运费(元)"
                  inputMode="decimal"
                  value={priceText}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setPriceText(raw);
                    // 敲到一半的「12.」不能抛
                    setPrice(Money.tryFromMajor(raw) ?? '');
                    // 手动改过就钉住,后面的试算不再覆盖它
                    setPriceSource('MANUAL');
                  }}
                  className={`${INPUT} text-right font-mono`}
                />
              </Field>
            </div>

            {/*
            ★ 说清楚这个数是**哪来的**。
              三种来源要人做的判断完全不同:规则算的可以放心用;
              上次那个价要确认还作不作数;自己改的要担责任。
              只显示一个数字的话,客服无从判断能不能就这么发出去。
          */}
            <div className="col-span-8 self-end pb-1 text-xs">
              {priceSource === 'MANUAL' ? (
                <span className="text-amber-700 dark:text-amber-400">
                  手工改过 —— 按这个金额建单,不再走计价规则
                  <QuoteHint quote={quote} price={price} onUse={useQuotedPrice} />
                </span>
              ) : priceSource === 'CONTACT' ? (
                <span className="text-brand-700 dark:text-brand-400">
                  上次给「{picked?.name}」就是这个价 —— 改一下就按新的算
                  <QuoteHint quote={quote} price={price} onUse={useQuotedPrice} />
                </span>
              ) : quote?.matched ? (
                <span className="text-slate-400">
                  按计价规则算的
                  {quote.breakdown?.lines.map((l, i) => (
                    <span key={`${l.code}-${i}`} className="ml-2">
                      {l.label} {money(String(l.amount))}
                    </span>
                  ))}
                </span>
              ) : (
                <span className="text-amber-700 dark:text-amber-400">
                  {quote?.reason ?? '没有匹配的计价规则 —— 留空建出来运费会是 0,可以在这里直接填'}
                </span>
              )}
            </div>

            <div className="col-span-8">
              {/*
            ★ 区域这一行平时收起来。

              它的默认值「自动判断」在几乎每一单上都对,而摆一个不用动的
              下拉框在建单页上,占的是客服每一单都要扫过去的注意力。
              手机上这一屏本来就长,更该省。

              但**不能删掉**:代发、地址写得含糊、或者自动判错的时候,
              手工指定是唯一的出口。所以是「收起」不是「去掉」——
              而且选过之后不再收起:人已经表过态了,再藏起来
              就是把他的决定藏起来。
          */}
              {!zoneOpen && !f.zoneId ? null : (
                <Field label="配送区域" hint="留空则按地址自动判">
                  <select
                    aria-label="配送区域"
                    value={f.zoneId}
                    onChange={(e) => setF({ ...f, zoneId: e.target.value })}
                    className={INPUT}
                  >
                    <option value="">自动判断</option>
                    {/*
                ★ 只列**在用**的区域。
                  停用的区域不该能被派新单进去 —— 那是「这一片我们现在不送」
                  的意思,而下拉里列着它,手一滑就建出一张送不出去的单。

                  顺带治了一个性能问题:开发库里堆了一千多个测试区域,
                  每敲一个字都要重渲染一遍这个 select,整个对话框卡到
                  按钮点不动 —— 表现是「建单按钮没反应」,
                  谁也不会想到是下拉框太长。
              */}
                    {zones
                      .filter((z) => z.isActive)
                      .map((z) => (
                        <option key={z.id} value={z.id}>
                          {z.name}
                        </option>
                      ))}
                  </select>
                </Field>
              )}
            </div>

            {/*
            ★ 货到付款是一个**明确的勾**,不是「金额填了就算」。
              漏填和「这单不代收」长得一模一样,而两者差着一整单货款:
              司机不会去要钱,货给出去了,钱没了。
          */}
            <div className="col-span-6">
              <label className="flex items-center gap-2 py-1">
                <input
                  type="checkbox"
                  aria-label="货到付款"
                  checked={cod}
                  onChange={(e) => {
                    setCod(e.target.checked);
                    // 取消勾选就把金额清掉 —— 留着的话下次勾上会带出一个上次的数
                    if (!e.target.checked) {
                      setCodPending(false);
                      setF((s0) => ({ ...s0, codAmount: '' }));
                    }
                  }}
                />
                {/*
                ★ 这两个 label 用红色。
                  它们和「邮编」不是一个量级:代收漏填 = 货给出去了钱没收到,
                  特殊物品漏标 = 司机不知道车上有要冷藏的东西。
                  和别的字段长得一样的话,填表的人会一路 Tab 过去。
              */}
                <span className="whitespace-nowrap text-xs font-medium text-red-600 dark:text-red-400">
                  货到付款
                </span>
                <span className="hidden text-[11px] text-slate-400 sm:inline">
                  送到时向收件人收钱
                </span>
              </label>

              {cod && (
                <div className="mt-1 pl-6">
                  {/*
                  ★ 「金额待定」是勾出来的,不是靠留空默认的。
                    留空即待定的话,漏填和「等结算」又长得一样了 ——
                    而这颗勾正是为了把这两件事分开。
                */}
                  <label className="mb-1 flex items-center gap-2 py-0.5">
                    <input
                      type="checkbox"
                      aria-label="金额待定"
                      checked={codPending}
                      onChange={(e) => {
                        setCodPending(e.target.checked);
                        if (e.target.checked) setF((s0) => ({ ...s0, codAmount: '' }));
                      }}
                    />
                    <span className="text-xs text-slate-600 dark:text-slate-300">
                      金额待定(还没算出来)
                    </span>
                    <span className="hidden text-[11px] text-slate-400 sm:inline">
                      小票上留空手写,司机送达时按商家确认的数录入
                    </span>
                  </label>
                  <Field
                    label="要收多少(元)"
                    required={!codPending}
                    hint={
                      codPending
                        ? '待定 —— 司机端显示「联系商家确认」,小票上是一条空线'
                        : '必填 —— 司机在门口看到的就是这个数'
                    }
                  >
                    <input
                      inputMode="decimal"
                      aria-label="代收金额(元)"
                      placeholder={codPending ? '等金额出来再填' : '120'}
                      disabled={codPending}
                      value={f.codAmount}
                      onChange={(e) => setF({ ...f, codAmount: e.target.value })}
                      className={`${INPUT} disabled:opacity-50`}
                    />
                  </Field>
                </div>
              )}
            </div>

            {/*
            ★ 特殊物品:分类是每家自己在「设置 → 字典」里加的。
              写死一份等于逼客户用我们的词,或者等我们发版。

            ★ 用一排能点的标签而不是 <select multiple>:
              原生多选要按住 Ctrl 才能选第二项,松手就只剩一项 ——
              而漏掉「冻品」的后果是司机不知道车上有要冷藏的东西。
          */}
            {specialItemsOn && (
              <div className="col-span-6">
                <label className="flex items-center gap-2 py-1">
                  <input
                    type="checkbox"
                    aria-label="有特殊物品"
                    checked={specialItems.length > 0}
                    onChange={(e) => {
                      if (!e.target.checked) setSpecialItems([]);
                      else if (specialDict[0]) setSpecialItems([specialDict[0].code]);
                    }}
                    disabled={specialDict.length === 0}
                  />
                  <span className="whitespace-nowrap text-xs font-medium text-red-600 dark:text-red-400">
                    有特殊物品
                  </span>
                  <span className="hidden text-[11px] text-slate-400 sm:inline">
                    {specialDict.length === 0 ? '去「设置 → 字典」里加' : '司机端会高亮提醒'}
                  </span>
                </label>

                {specialItems.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-2 pl-6">
                    {specialDict.map((d) => {
                      const on = specialItems.includes(d.code);
                      return (
                        <button
                          key={d.code}
                          type="button"
                          aria-label={`特殊物品 ${d.label}`}
                          aria-pressed={on}
                          onClick={() =>
                            setSpecialItems((cur) =>
                              cur.includes(d.code)
                                ? cur.filter((c) => c !== d.code)
                                : [...cur, d.code],
                            )
                          }
                          className={`rounded-full border px-2.5 py-1 text-xs ${
                            on
                              ? 'border-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300'
                              : 'border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300'
                          }`}
                        >
                          {d.icon ? `${d.icon} ` : ''}
                          {d.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <div className="col-span-12">
              {/*
              ★ 这个框写的是**订单备注**(receiverNote),不再是 itemDescription。
 
                用户原话:「订单里面的备注就应该只有一个字段,
                不管怎么操作都是操作同一个属性」。
 
                在这之前,这个框叫「物品 / 备注」、写进 itemDescription,
                而收件人身上那句长期成立的交代(门禁密码)并进的是
                receiverNote —— 两股字进了两个字段,于是屏幕上、票上
                又是两条备注。现在都落在同一列。
 
              ★ 顺带修掉一个更直白的毛病:`receiverNote` 本来就在这个弹窗的
                state 里、提交时也会带上,**却没有任何地方能填** ——
                走界面建的单,这一列永远是空的。
            */}
              <Field label="备注">
                <input
                  aria-label="备注"
                  value={f.receiverNote}
                  onChange={(e) => setF({ ...f, receiverNote: e.target.value })}
                  placeholder="放门口、先别送、大门密码 1234……"
                  className={INPUT}
                />
              </Field>
            </div>
            {/*
              ★ 「物品」和「备注」分成两个框,不再是一个「物品 / 备注」。
 
                合在一起的时候人会把交代的事打进去(用户就是这么干的),
                而那句话进的是 itemDescription —— 司机端根本不读这一列,
                于是他一个字都看不见。名字里带个斜杠,就等于让人猜该填哪边。
 
              ★ 两个都印,但印在不同的块上:备注贴着地址(到门口要照着做的),
                物品在单号件数那一块(点货时要核的)。
            */}
            <div className="col-span-12">
              <Field label="物品">
                <input
                  aria-label="物品"
                  value={f.itemDescription}
                  onChange={(e) => setF({ ...f, itemDescription: e.target.value })}
                  placeholder="里面是什么,例如:两箱冻品"
                  className={INPUT}
                />
              </Field>
            </div>
          </div>

          {/*
          ★ 坐标要在建单时就点掉。
            不点的话这单会进复核队列 —— 上不了地图,司机也导航不了,
            而调度多半到派单时才发现,那时候电话早挂了。
        */}
          <div className="mt-2.5 rounded-lg border border-slate-200 p-2.5 dark:border-slate-700">
            <div className="flex items-center justify-between gap-2">
              {/*
              ★ 「位置」和「区域」并成一行。
              
                两个都是**地址判出来的东西**,而且都只有一行字 ——
                各占一整行是在拿垂直空间换不了信息。手机上这一屏有十几个字段,
                每省一行都是实的。窄屏放不下时自己折行,不硬挤。
            */}
              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5">
                <div className="text-xs text-slate-700 dark:text-slate-300">
                  位置{' '}
                  {lat != null ? (
                    <span className="ml-1 font-mono text-xs text-green-600 dark:text-green-400">
                      {lat.toFixed(5)}, {lng!.toFixed(5)}
                    </span>
                  ) : (
                    <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">
                      未获取坐标
                    </span>
                  )}
                </div>
                {/*
                ★ 区域收起时只留这一句。默认值「自动判断」在几乎每一单上都对,
                  摆一个不用动的下拉框占的是客服每一单都要扫过去的注意力。
                  但代发、地址含糊、自动判错时,手工指定是唯一的出口 ——
                  所以是收起不是去掉,而且展开过就不再收回去。
              */}
                {!zoneOpen && !f.zoneId && (
                  <div className="text-xs text-slate-500">
                    区域 <span className="text-slate-600 dark:text-slate-400">按地址自动判断</span>{' '}
                    <button
                      type="button"
                      onClick={() => setZoneOpen(true)}
                      className="text-brand-600 hover:underline dark:text-brand-400"
                    >
                      手动指定
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowMap((v) => !v)}
                className="shrink-0 text-sm text-brand-600 hover:underline dark:text-brand-400"
              >
                {showMap ? '收起地图' : lat != null ? '重新点' : '在地图上点'}
              </button>
            </div>
            {showMap && (
              <div className="mt-3">
                <Suspense
                  fallback={
                    <div className="grid h-64 place-items-center text-slate-400">地图加载中…</div>
                  }
                >
                  <LocationPicker
                    lat={lat}
                    lng={lng}
                    onPick={(a, b) => {
                      setLat(a);
                      setLng(b);
                      setErr(null);
                    }}
                  />
                </Suspense>
              </div>
            )}
          </div>

          <div className="h-3" />
        </div>

        {/* 脚:固定。底部留安全区 */}
        <div
          className="shrink-0 border-t border-slate-100 px-4 pt-2.5 dark:border-slate-700"
          style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}
        >
          {/*
            ★ 「今天已经给这个人建过一单」摆在按钮**正上方**,不是弹另一个框。
              再弹一层的话,人要在第二个框里凭记忆判断第一个框里填的是什么;
              放在这儿,单号和表单同屏,她抬眼就能对。
          */}
          {dupWarn && (
            <div
              aria-label="今天已有未完成的单"
              className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs
                         dark:border-amber-800 dark:bg-amber-950/40"
            >
              <div className="text-amber-900 dark:text-amber-200">
                {f.receiverName.trim()} 今天已经有一张没走完的单:
                <span className="font-medium">{dupWarn.orderNo}</span>
                {dupWarn.status && (
                  <span className="ml-1">
                    · {ADMIN_STATUS_LABEL[dupWarn.status as OrderStatus] ?? dupWarn.status}
                  </span>
                )}
                {dupWarn.createdAt && (
                  <span className="ml-1">
                    ·{' '}
                    {new Date(dupWarn.createdAt).toLocaleTimeString('zh-CN', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    建的
                  </span>
                )}
              </div>
              {/* ★ 两条路都写出来:确实要两单的照建,建错了的去看那一张 */}
              <div className="mt-1 text-amber-800/80 dark:text-amber-300/80">
                真的是两单就继续;如果刚才那次以为没建成,关掉这个框去列表里找那一张
              </div>
            </div>
          )}

          {err && (
            <div
              aria-live="polite"
              className="mb-2 rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300"
            >
              {err}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-500">
              取消
            </button>
            <button
              onClick={() => void save(dupWarn != null)}
              disabled={busy || !canSave}
              className={`rounded-lg px-4 py-1.5 text-sm font-medium text-white disabled:opacity-40 ${
                dupWarn ? 'bg-amber-600' : 'bg-brand-600'
              }`}
            >
              {busy ? '建单中…' : dupWarn ? '确认再建一单' : '建单'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const INPUT =
  'w-full rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm ' +
  'dark:border-slate-600 dark:bg-slate-800';

function Field({
  label,
  hint,
  required,
  emphasis,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  /**
   * 标签用强调色。
   *
   * ★ 和「货到付款」「有特殊物品」同一个红,不是新造一种颜色 ——
   *   这一屏上红色已经有确定的意思:**这一项弄错了要赔钱或者要跑第二趟**。
   *   运费属于这一类:客服要照着它在电话里报价,报错了是真金白银。
   */
  emphasis?: boolean;
  children: React.ReactNode;
}) {
  /*
   * ★ 提示放在输入框**下面**,不能放在标签和输入框中间。
   *
   *   放中间的话,有提示的字段(手机号)比没提示的(收件人)整整低一行 ——
   *   同一行里两个输入框**对不齐**,看起来像布局坏了。
   *   而这一屏里带提示的字段将近一半,于是每一行都错开一点。
   *
   * ★ 顺带把行距收紧:这个弹窗字段多,一屏放不下就得滚,
   *   而客服正拿着电话 —— 少滚一次就少一次「刚才那个框在哪」。
   */
  return (
    <label className="block text-xs">
      <span className={emphasis ? 'font-medium text-red-600 dark:text-red-400' : 'text-slate-500'}>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      <div className="mt-0.5">{children}</div>
      {hint && (
        <span className="mt-0.5 block text-[11px] leading-tight text-slate-400">{hint}</span>
      )}
    </label>
  );
}

/**
 * 「规则算出来其实是另一个数」。
 *
 * ★ 只在**不一样**的时候出现。一样的时候提示它,是把一句废话
 *   放在客服每天要看几十次的地方 —— 而废话看多了,真的那次也会被略过。
 */
function QuoteHint({
  quote,
  price,
  onUse,
}: {
  quote: Quote | null;
  price: number | '';
  onUse: () => void;
}) {
  if (!quote?.matched) return null;
  const q = quote.customerPrice;
  if (q == null || price === '' || Number(q) === Number(price)) return null;
  return (
    <>
      {' · '}
      <span className="text-slate-500">按规则应为 {money(String(q))}</span>{' '}
      <button
        type="button"
        onClick={onUse}
        className="text-brand-600 underline dark:text-brand-500"
      >
        用它
      </button>
    </>
  );
}
